import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { type ThreeEvent } from '@react-three/fiber';
import type { ImageProps, Number3 } from '../../types';
import { useAbyssViewerContext } from '../AbyssViewerContext';
import { useOnClick } from '../useOnClick';
import { ViewerLayer } from '../ViewerLayer';
import { ImageBillboard } from '../ImageBillboard';
import { DirectionArrow } from '../Enums';
import { RectilinearImage } from './RectilinearImage';

export type NeighbouringImageProps = {
  image: ImageProps;
  key: string;
  position: Number3;
  rotation: Number3;
  scale: Number3;
  distance: number;
  color: number;
  hoveredColor: number;
  opacity: number;
  lineWidth: number;
  hoveredLineWidth: number;
  distanceToCenter: number;
  distanceToCenter2: number;
  directionArrow: DirectionArrow;
  shouldShowImage?: boolean;
  shouldShowFrame?: boolean;
  imageOpacity?: number;
  onClick: (image: NeighbouringImageProps) => void;
  onHover: (image: NeighbouringImageProps | undefined) => void;
};

export const NeighbouringImage = (neigbouringImage: NeighbouringImageProps) => {
  const {
    image,
    position,
    rotation,
    scale,
    distance,
    color,
    hoveredColor,
    opacity,
    lineWidth,
    hoveredLineWidth,
    shouldShowImage = false,
    shouldShowFrame = true,
    imageOpacity = 0.1,
    onClick,
    onHover,
  } = neigbouringImage;
  const { width, height, cx, cy } = image;
  const { sharedGeometries, clippingPlanes } = useAbyssViewerContext();
  const { pictureFrameLines, hoveredPictureFrameLines } = sharedGeometries ?? {};

  const lineSegmentsRef = useRef<THREE.LineSegments | null>(null);

  const layers = useMemo(() => {
    const layers = new THREE.Layers();
    layers.enable(ViewerLayer.Default);
    layers.enable(ViewerLayer.AutoPickable);
    layers.enable(ViewerLayer.SphericalViewerLayer);
    return layers;
  }, []);

  const shouldHandle = useCallback((event: ThreeEvent<MouseEvent>) => {
    const { altKey, ctrlKey, metaKey, shiftKey, button } = event;
    const isLeftButton = button === 0;
    if (!isLeftButton || altKey || ctrlKey || metaKey || shiftKey) {
      return false;
    }
    return true;
  }, []);

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (onClick) {
        event.stopPropagation();
        onClick(neigbouringImage);
      }
    },
    [onClick, neigbouringImage]
  );

  const [isHovered, setIsHovered] = useState<boolean>(false);

  const onPointerOver = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      if (event.intersections[0].object === lineSegmentsRef.current) {
        setIsHovered(true);
        event.stopPropagation();
        onHover(neigbouringImage);
      }
    },
    [neigbouringImage, onHover]
  );

  const onPointerOut = useCallback(() => {
    setIsHovered(false);
    onHover(undefined);
  }, [onHover]);

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: handleClick, shouldHandle });

  return (
    <>
      {(shouldShowFrame || isHovered) && (
        <group position={position} rotation={rotation} scale={scale}>
          <lineSegments
            ref={lineSegmentsRef}
            geometry={pictureFrameLines}
            layers={layers}
            position={[-cx / width, cy / height, 0]}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerOver={onPointerOver}
            onPointerOut={onPointerOut}
          >
            <lineBasicMaterial
              attach="material"
              clippingPlanes={clippingPlanes}
              color={isHovered ? hoveredColor : color}
              transparent
              opacity={opacity}
              linewidth={isHovered ? hoveredLineWidth : lineWidth}
            />
          </lineSegments>
          {isHovered && (
            <ImageBillboard
              position={[0, 0.15, 0]}
              imagePosition={image.position}
              name={image.name}
            />
          )}
        </group>
      )}
      {shouldShowImage && (
        <Suspense>
          <RectilinearImage
            {...image}
            distance={distance}
            opacity={imageOpacity}
            onClick={() => onClick(neigbouringImage)}
            onHover={(current) => setIsHovered(current)}
          />
        </Suspense>
      )}
      {isHovered && (
        <lineSegments
          geometry={hoveredPictureFrameLines}
          position={image.position}
          rotation={rotation}
          scale={[1, 1, distance]}
          layers={layers}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
        >
          <lineBasicMaterial
            attach="material"
            clippingPlanes={clippingPlanes}
            color={hoveredColor}
            transparent
            opacity={opacity}
            linewidth={hoveredLineWidth}
          />
        </lineSegments>
      )}
    </>
  );
};
